/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { useState, useMemo } from 'react';
import { Button, Progress } from 'reactstrap';
import { Table, type ColumnDef } from '@g17eco/molecules/table';
import { DebouncedSearchBox } from '@g17eco/molecules/search/DebouncedSearchBox';
import LoadingButton from '@g17eco/molecules/button/LoadingButton';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { InsightStatusBadge } from './partials/InsightStatusBadge';
import { ImpactBadge } from './partials/ImpactBadge';
import { InsightViewModal } from '../partials/InsightViewModal';
import { InsightStatus, type InsightSynthesis } from '@g17eco/types/insights-synthesis';

interface InsightsTableProps {
  insights: InsightSynthesis[];
  onApprove: (insight: InsightSynthesis) => Promise<void>;
  onReject: (insight: InsightSynthesis) => Promise<void>;
  approvingId: string | null;
  rejectingId: string | null;
}

const getScoreColor = (score: number): string => {
  if (score >= 0.8) return 'success';
  if (score >= 0.6) return 'info';
  if (score >= 0.4) return 'warning';
  return 'danger';
};

const QualityScoreIndicator = ({ score }: { score: number }) => {
  const percentage = Math.round(score * 100);
  const color = getScoreColor(score);

  return (
    <SimpleTooltip text={`Quality confidence: ${percentage}%`}>
      <div className='d-flex align-items-center gap-2'>
        <Progress value={percentage} color={color} className='flex-grow-1' style={{ height: '6px' }} />
        <span className='small fw-medium text-nowrap'>{percentage}%</span>
      </div>
    </SimpleTooltip>
  );
};

export const InsightsTable = ({
  insights,
  onApprove,
  onReject,
  approvingId,
  rejectingId,
}: InsightsTableProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInsight, setSelectedInsight] = useState<InsightSynthesis | null>(null);

  const filteredInsights = useMemo(() => {
    if (!searchTerm) return insights;

    const term = searchTerm.toLowerCase();
    return insights.filter(
      (insight) =>
        insight.content.title.toLowerCase().includes(term) ||
        insight.content.description.toLowerCase().includes(term) ||
        insight.source.metadata.sectionType.toLowerCase().includes(term)
    );
  }, [insights, searchTerm]);

  const columns = useMemo<ColumnDef<InsightSynthesis>[]>(
    () => [
      {
        accessorKey: 'content.title',
        header: 'Title',
        cell: ({ row }) => (
          <Button color='link' className='p-0 text-start' onClick={() => setSelectedInsight(row.original)}>
            {row.original.content.title}
          </Button>
        ),
        meta: { cellProps: { style: { minWidth: 200 } } },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <InsightStatusBadge status={row.original.status} />,
      },
      {
        id: 'qualityScore',
        header: 'Quality',
        accessorFn: (row) => {
          // Compute average quality score from confidence, relevance, and clarity
          const { confidence, relevance, clarity } = row.content;
          return (confidence + relevance + clarity) / 3;
        },
        cell: ({ row }) => {
          const { confidence, relevance, clarity } = row.original.content;
          const qualityScore = (confidence + relevance + clarity) / 3;
          return <QualityScoreIndicator score={qualityScore} />;
        },
        meta: { cellProps: { style: { minWidth: 120 } } },
      },
      {
        accessorKey: 'content.impact',
        header: 'Impact',
        cell: ({ row }) => <ImpactBadge impact={row.original.content.impact} />,
      },
      {
        id: 'actions',
        header: '',
        meta: { cellProps: { style: { width: 120 } } },
        cell: ({ row }) => {
          const isPending = row.original.status === InsightStatus.PendingApproval;
          const isApproving = approvingId === row.original._id;
          const isRejecting = rejectingId === row.original._id;
          const isDisabled = !isPending || isApproving || isRejecting;

          return (
            <div className='d-flex gap-1'>
              <LoadingButton
                color='success'
                size='sm'
                onClick={() => onApprove(row.original)}
                loading={isApproving}
                disabled={isDisabled}
                title='Approve'
              >
                <i className='fa fa-check' />
              </LoadingButton>
              <LoadingButton
                color='danger'
                size='sm'
                onClick={() => onReject(row.original)}
                loading={isRejecting}
                disabled={isDisabled}
                title='Reject'
              >
                <i className='fa fa-times' />
              </LoadingButton>
            </div>
          );
        },
      },
    ],
    [approvingId, rejectingId, onApprove, onReject]
  );

  return (
    <div>
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <h5 className='mb-0'>Generated Insights ({filteredInsights.length})</h5>
        <div style={{ minWidth: '250px' }}>
          <DebouncedSearchBox onTextChange={setSearchTerm} placeholder='Search insights...' />
        </div>
      </div>

      <Table
        columns={columns}
        data={filteredInsights}
        pageSize={10}
        responsive
        noData={
          <div className='text-muted py-4'>
            {insights.length === 0 ? 'No insights generated yet.' : 'No insights match your search.'}
          </div>
        }
        sortBy={[{ id: 'qualityScore', desc: true }]}
      />

      {selectedInsight && (
        <InsightViewModal
          insight={selectedInsight}
          isOpen={true}
          onClose={() => setSelectedInsight(null)}
          onApprove={onApprove}
          onReject={onReject}
          isApproving={!!approvingId && approvingId === selectedInsight._id}
          isRejecting={!!rejectingId && rejectingId === selectedInsight._id}
        />
      )}
    </div>
  );
};
